/**
 * The one place `localStorage` is touched.
 *
 * Two small modules keep something here — the sign-in friction record and the last-used
 * XI per team — and both want the same two methods and the same guard, so the guard lives
 * once rather than in each of them.
 *
 * **Reading `window.localStorage` can throw**, which is the whole reason this exists:
 * Safari with cookies blocked raises a `SecurityError` on the *property access*, before
 * any method is called. Both callers are conveniences — a measurement and a preselected
 * XI — so the right failure is to lose them and carry on, never to take down the screen
 * they sit on. A scorer locked out of a match because a browser refused a storage probe
 * would be an absurd trade.
 */

/** The slice of `localStorage` anything here needs. An interface so tests need no browser. */
export interface Store {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

/** Live for this page only. What a browser that refuses storage falls back to. */
function memoryStore(): Store {
  const memory = new Map<string, string>();
  return {
    getItem: (key) => memory.get(key) ?? null,
    setItem: (key, value) => void memory.set(key, value),
  };
}

let resolved: Store | undefined;

export function localStore(): Store {
  if (resolved !== undefined) return resolved;
  try {
    const store = window.localStorage;
    // Probed, not merely accessed: some configurations only throw on use.
    store.getItem("sportsos.probe");
    resolved = store;
  } catch {
    resolved = memoryStore();
  }
  return resolved;
}
